/**
 * Gateway / proxy failures must never surface the infrastructure's own text.
 *
 * Staging sits behind Cloudflare, which answers API clients with an RFC 9457
 * JSON body whose `detail` field is the SAME key our FastAPI errors use — so
 * the interceptor showed "The origin web server returned an invalid or
 * incomplete response to Cloudflare…" as if the app had said it (functional
 * team 2026-09-30, report preview). A load balancer's HTML 502 page is the
 * same class of failure. This maps every such response to a neutral message;
 * the backend's own `detail` passes through untouched.
 */

/** Proxy statuses our backend never emits (Cloudflare 52x range). */
const EDGE_ONLY = new Set([520, 521, 522, 523, 524, 525, 526, 527, 530]);
/** Gateway statuses that may come from the proxy OR (rarely) the app. */
const GATEWAY = new Set([502, 503, 504]);
/** Statuses that mean "the server was too slow", not "the server is down". */
const TIMEOUTS = new Set([504, 522, 524]);

export const GATEWAY_TIMEOUT_MESSAGE =
  'The server took too long to respond. Please try again — if it keeps happening, ' +
  'narrow the selection (POV, members) or contact your administrator.';
export const GATEWAY_UNAVAILABLE_MESSAGE =
  'The server is temporarily unavailable or returned an incomplete response. ' +
  'Please try again in a moment.';

const INFRA_WORDS = /cloudflare|origin web server|\bray id\b|nginx|bad gateway|gateway time-?out/i;

function mentionsInfra(value: unknown): boolean {
  return typeof value === 'string' && INFRA_WORDS.test(value);
}

/**
 * Neutral replacement for a gateway/proxy error, or null when the response
 * is a genuine application error whose `detail` should be shown as-is.
 */
export function gatewayErrorMessage(status: number | undefined, body: unknown): string | null {
  const obj = body && typeof body === 'object' ? (body as Record<string, unknown>) : null;
  const detail = obj?.detail;
  const fromEdge =
    (status != null && EDGE_ONLY.has(status)) ||
    obj?.cloudflare_error === true ||
    mentionsInfra(detail) ||
    mentionsInfra(obj?.title) ||
    mentionsInfra(obj?.type) ||
    typeof body === 'string' && (mentionsInfra(body) || /^\s*<(!doctype|html)/i.test(body));
  // A bare gateway status with no app-authored detail (HTML page, empty body).
  const bareGateway =
    status != null && GATEWAY.has(status) && !(typeof detail === 'string' && detail.trim());
  if (!fromEdge && !bareGateway) return null;
  return status != null && TIMEOUTS.has(status)
    ? GATEWAY_TIMEOUT_MESSAGE
    : GATEWAY_UNAVAILABLE_MESSAGE;
}
